import { ChangeDetectionStrategy, Component, computed, effect, inject, input, OnInit, signal } from '@angular/core';
import { ProductsSummaryComponent } from './products-summary.component';
import { NewClientLogic, NewClientStateService } from '@mwe/services';
import { HeadlineService } from '../../headline/headline.service';
import { IProduktAuswahlDetails, Sparte, TextNodeType } from '@mwe/models';
import { getAddressGroupFormatted, getContractStartInfoFromTariffs, isProductCategory } from '@mwe/utils';
import { AddressComponent } from '../../address/address.component';
import { HeadlineComponent } from '../../headline/headline.component';
import { TariffSelectionContainerComponent } from '../../tariff/tariff-selection-container/tariff-selection-container.component';
import { BasicCardComponent } from '../../cards/basic-card/basic-card.component';
import { WizardProductCardComponent } from '../../product-card/wizard-product-card/wizard-product-card.component';
import { BasicCardTitleComponent } from '../../cards/basic-card/basic-card-title/basic-card-title.component';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { InfoboxComponent } from '../../infobox/infobox.component';
import { TariffCardWaermeComponent } from '../../tariff/products/waerme/tariff-card-waerme.component';
import { ProductsSummaryInvoiceOldComponent } from './products-summary-invoice-old.component';
import { ProductSummaryContractInfoComponent } from './product-summary-contract-info.component';
import { ProductCategoryEnum } from '@mwe/constants';
import { CorrelatedCompanyReferencePipe, FormatPhoneNumberPipe } from '@mwe/ui/pipes';
import { ProductIconComponent } from '../../product-icon/product-icon.component';
import { InfoStep } from '../../backend-process/backend-process-presentation/backend-process-presentation.component';
import { NextStepsComponent } from '../../next-steps/next-steps.component';

@Component({
  selector: 'mwe-products-summary-redesign',
  templateUrl: './products-summary-redesign.component.html',
  imports: [
    AddressComponent,
    HeadlineComponent,
    TariffSelectionContainerComponent,
    BasicCardComponent,
    WizardProductCardComponent,
    BasicCardTitleComponent,
    TranslateDirective,
    InfoboxComponent,
    TariffCardWaermeComponent,
    ProductsSummaryInvoiceOldComponent,
    TranslatePipe,
    FormatPhoneNumberPipe,
    ProductSummaryContractInfoComponent,
    TariffSelectionContainerComponent,
    ProductIconComponent,
    NextStepsComponent,
    CorrelatedCompanyReferencePipe,
  ],
  providers: [HeadlineService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductsSummaryRedesignComponent extends ProductsSummaryComponent implements OnInit {
  private readonly newClientLogic = inject(NewClientLogic);
  private readonly newClientStateService = inject(NewClientStateService);
  protected readonly headlineService = inject(HeadlineService);

  readonly showFormCardInfo = input<boolean>(true);
  readonly headlineReference = input<TextNodeType>('h2');
  readonly showAdditionalInfo = input<boolean>();
  readonly isSummaryProductsFromWevTitleVisible = input<boolean>(false);
  readonly isSummary = input<boolean>(false);
  readonly infoSteps = input<InfoStep[]>([]);
  readonly emobTariffChange = input<boolean>(false);
  readonly summaryContractCardTitle = input('summary.contractDataCardTitle');

  protected readonly Sparte = Sparte;

  readonly selectedProducts = signal<[string, IProduktAuswahlDetails][]>(null);
  readonly startDelivery = signal<string>(null);

  readonly tariffContractInfos = computed(() =>
    this.selectedProducts().map(data => ({
      title: data?.[1]?.tarif?.tarifName ?? '',
      startDate: data?.[1]?.bedingungen?.angebotsdatum ?? '',
      commitment: data?.[1]?.tarif?.tarifOptionen?.find(option => option.art === 'Vertragsbindung')?.optionLabel ?? '',
      priceGuaranteeDate: data?.[1]?.bedingungen?.preisbindungsende ?? '',
      contractStartDateDetailedInfo: getContractStartInfoFromTariffs(data?.[1].tarif?.ISUTarifKey, this.selectedTariffs()),
      startDelivery: this.startDelivery(),
    })),
  );
  readonly formattedAddressC = computed(() => getAddressGroupFormatted(this.address(), true));
  override readonly formattedAddress = computed(() => getAddressGroupFormatted(this.address()));
  readonly showBenefitsCard = computed(
    () => !!this.voucherInfo() && this.nonFernwaermeProducts()?.length > 0 && !this.newClientLogic.isSuperschnellProcess(),
  );
  readonly showAdditionalInfoBox = computed(() => {
    if (typeof this.showAdditionalInfo() !== 'undefined') {
      return this.showAdditionalInfo();
    }
    if (this.newClientLogic.isSuperschnellProcess() || (!this.nonFernwaermeProducts()?.length && this.fernwaermeProducts()?.length)) {
      return false;
    }
    return this.showMeterReading() || !!this.mgmCode() || this.showFormCardInfo();
  });
  readonly providerChange = signal<boolean>(false);

  constructor() {
    super();
    effect(() => {
      this.headlineService.setReferenceHeadline(this.headlineReference());
    });
  }

  ngOnInit(): void {
    this.selectedProducts.set(
      (Object.entries(this.newClientStateService?.productSelection ?? {}) as unknown as [string, IProduktAuswahlDetails][])
        ?.filter(contents => !!contents?.[1])
        ?.filter(([sparte]) => !isProductCategory(sparte, ProductCategoryEnum.FERNWAERME)),
    );

    this.startDelivery.set(
      this.newClientStateService?.contractDetails?.earliestSupplyAsPossible
        ? 'summary.contract.start-delivery-value'
        : this.newClientStateService?.registrationDate,
    );

    this.providerChange.set(this.newClientStateService?.contractDetails?.providerChange);
  }
}
